import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { TaskySelect } from '../tasky';
import adReportService, {
  AdReportFrequency,
  AdReportSetting,
  AdReportSettingInput,
  AdReportTarget,
} from '../../services/api/adReportService';

const CARD = 'bg-white border border-gray-200 rounded-lg p-5 mb-6';
const HEAD = 'flex items-start justify-between gap-4 flex-wrap mb-1';
const TITLE = 'text-[16px] font-semibold text-gray-800 m-0';
const NOTE = 'text-[12px] text-gray-500 leading-relaxed m-0 mt-1';
const GRID = 'grid gap-4 mt-5 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]';
const FIELD = 'flex flex-col gap-1.5';
const LABEL = 'text-[12px] font-medium text-gray-700';
const HINT = 'text-[11px] text-gray-500 leading-relaxed';
const INPUT =
  'h-9 px-3 border border-gray-300 rounded-md text-[13px] text-gray-800 bg-white ' +
  'focus:outline-none focus:ring-2 focus:ring-[#0f4c5c]/30 focus:border-[#0f4c5c]';
const FOOT = 'flex items-center gap-3 flex-wrap mt-5 pt-4 border-t border-gray-100';
const STATUS_OK = 'text-[12px] text-emerald-700';
const STATUS_NG = 'text-[12px] text-red-600';
const BADGE = 'text-[11px] px-2 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700';

const FREQUENCY_OPTIONS: { value: AdReportFrequency; label: string }[] = [
  { value: 'daily', label: '毎日' },
  { value: 'weekly', label: '毎週' },
  { value: 'monthly', label: '毎月' },
];

/** 設定済みの秘匿値は伏字で表示する。入力されなければ送らない＝既存値が残る。 */
const SECRET_PLACEHOLDER = '設定済み（変更する場合のみ入力）';

interface Props {
  /** 組織のリーダーだけが変更・テスト送信できる。 */
  canEdit: boolean;
}

const AdReportSettingsPanel: React.FC<Props> = ({ canEdit }) => {
  // 先頭は組織全体。読めなければ組織全体だけで動かす（従来の設定は失わない）。
  const [targets, setTargets] = useState<AdReportTarget[]>([]);
  const [productId, setProductId] = useState<number | null>(null);
  const [setting, setSetting] = useState<AdReportSetting | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);

  // 秘匿値は入力されたときだけ送る。空文字のままなら「変更なし」。
  const [discordWebhook, setDiscordWebhook] = useState('');
  const [slackWebhook, setSlackWebhook] = useState('');
  const [chatworkToken, setChatworkToken] = useState('');

  const [frequency, setFrequency] = useState<AdReportFrequency>('daily');
  const [monthlyBudget, setMonthlyBudget] = useState('');
  const [targetCpa, setTargetCpa] = useState('');
  const [chatworkRoomId, setChatworkRoomId] = useState('');
  const [emails, setEmails] = useState('');

  const applySetting = useCallback((next: AdReportSetting) => {
    setSetting(next);
    setFrequency(next.frequency);
    setMonthlyBudget(next.monthly_budget !== null ? String(next.monthly_budget) : '');
    setTargetCpa(next.target_cpa !== null ? String(next.target_cpa) : '');
    setChatworkRoomId(next.chatwork_room_id ?? '');
    setEmails(next.email_recipients.join(', '));
    setDiscordWebhook('');
    setSlackWebhook('');
    setChatworkToken('');
  }, []);

  useEffect(() => {
    let active = true;
    adReportService
      .getAdReportTargets()
      .then((next) => {
        if (active) setTargets(next);
      })
      .catch((e) => console.error('Ad report targets load failed:', e));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setMessage(null);
    // 前の対象の値を残すと、読み込みに失敗したときにその値で別の対象を上書きできてしまう。
    setSetting(null);

    adReportService
      .getAdReportSetting(productId)
      .then((next) => {
        if (active) applySetting(next);
      })
      .catch(() => {
        if (active) setMessage({ text: 'レポート設定を読み込めませんでした。', ok: false });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [applySetting, loadAttempt, productId]);

  const numericOrNull = (value: string): number | null => {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const buildPayload = (overrides: AdReportSettingInput = {}): AdReportSettingInput => {
    const payload: AdReportSettingInput = {
      frequency,
      monthly_budget: numericOrNull(monthlyBudget),
      target_cpa: numericOrNull(targetCpa),
      chatwork_room_id: chatworkRoomId.trim() || null,
      email_recipients: emails
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean),
      ...(productId !== null ? { product_id: productId } : {}),
      ...overrides,
    };

    // 空欄のままの秘匿値はキーごと送らない。送ると既存値を null で潰してしまう。
    if (discordWebhook.trim()) payload.discord_webhook_url = discordWebhook.trim();
    if (slackWebhook.trim()) payload.slack_webhook_url = slackWebhook.trim();
    if (chatworkToken.trim()) payload.chatwork_api_token = chatworkToken.trim();

    return payload;
  };

  const save = async (overrides: AdReportSettingInput = {}) => {
    if (!canEdit || !setting || saving || testing) return;
    setSaving(true);
    setMessage(null);
    try {
      const next = await adReportService.updateAdReportSetting(buildPayload(overrides));
      applySetting(next);
      setMessage({ text: '保存しました。', ok: true });
    } catch (error: any) {
      const detail = error?.response?.data?.message;
      setMessage({ text: detail || '保存に失敗しました。入力内容をご確認ください。', ok: false });
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!canEdit || !setting || saving || testing) return;
    setTesting(true);
    setMessage(null);
    try {
      const result = await adReportService.sendAdReportTest(productId);
      setMessage({ text: result.message, ok: true });
    } catch (error: any) {
      const detail = error?.response?.data?.message;
      setMessage({ text: detail || 'テスト送信に失敗しました。', ok: false });
    } finally {
      setTesting(false);
    }
  };

  // 組織全体しか無ければ選ぶものが無い。
  const targetPicker = targets.length > 1 ? (
    <div className={`${FIELD} mt-3 max-w-xs`}>
      <label className={LABEL} htmlFor="ad-report-target">
        通知の対象
      </label>
      <TaskySelect
        id="ad-report-target"
        className="h-9 text-sm"
        value={productId === null ? '' : String(productId)}
        disabled={saving || testing}
        onChange={(e) => setProductId(e.target.value === '' ? null : Number(e.target.value))}
      >
        {targets.map((target) => (
          <option key={target.product_id ?? 'organization'} value={target.product_id ?? ''}>
            {target.linked === false ? `${target.name}（広告未連携）` : target.name}
          </option>
        ))}
      </TaskySelect>
    </div>
  ) : null;

  if (loading) {
    return (
      <div className={CARD} data-testid="ad-report-settings-loading">
        {targetPicker}
        <p className={NOTE}>レポート設定を読み込み中...</p>
      </div>
    );
  }

  if (!setting) {
    return (
      <div className={CARD} data-testid="ad-report-settings">
        <h2 className={TITLE}>通知連携・広告レポート</h2>
        {targetPicker}
        <p className={`${STATUS_NG} mt-3`} role="alert">{message?.text}</p>
        <Button variant="outline" size="sm" onClick={() => setLoadAttempt((value) => value + 1)}>
          再読み込み
        </Button>
      </div>
    );
  }

  const enabled = setting.enabled;
  const disabled = !canEdit || saving || testing;
  const hasSavedDestination = setting.has_discord_webhook || setting.has_slack_webhook
    || (setting.has_chatwork_token && Boolean(setting.chatwork_room_id))
    || setting.email_recipients.length > 0;
  const hasUnsavedChanges = frequency !== setting.frequency
    || numericOrNull(monthlyBudget) !== setting.monthly_budget
    || numericOrNull(targetCpa) !== setting.target_cpa
    || (chatworkRoomId.trim() || null) !== setting.chatwork_room_id
    || JSON.stringify(buildPayload().email_recipients) !== JSON.stringify(setting.email_recipients)
    || Boolean(discordWebhook.trim() || slackWebhook.trim() || chatworkToken.trim());
  const hasDestination = Boolean(discordWebhook.trim() || setting.has_discord_webhook
    || slackWebhook.trim() || setting.has_slack_webhook
    || (chatworkRoomId.trim() && (chatworkToken.trim() || setting.has_chatwork_token))
    || emails.split(',').some((email) => email.trim()));

  return (
    <div className={CARD} data-testid="ad-report-settings">
      <div className={HEAD}>
        <div>
          <h2 className={TITLE}>通知連携・広告レポート</h2>
          {setting?.last_sent_at && (
            <p className={NOTE}>
              最終送信: {new Date(setting.last_sent_at).toLocaleString('ja-JP')}
            </p>
          )}
          {targetPicker}
        </div>
        <div className="flex items-center gap-2">
          {enabled && <span className={BADGE}>配信中</span>}
          <Button
            variant={enabled ? 'outline' : 'default'}
            size="sm"
            disabled={disabled || (!enabled && !hasDestination)}
            onClick={() => save({ enabled: !enabled })}
          >
            {enabled ? '配信を停止' : '配信を開始'}
          </Button>
        </div>
      </div>

      <ul className="flex flex-wrap gap-2 list-none p-0 mt-3 mb-0 text-[12px] text-gray-600" aria-label="保存済みの通知連携">
        {[
          ['Discord', setting.has_discord_webhook],
          ['Slack', setting.has_slack_webhook],
          ['Chatwork', setting.has_chatwork_token && Boolean(setting.chatwork_room_id)],
          ['メール', setting.email_recipients.length > 0],
        ].map(([name, configured]) => (
          <li key={String(name)} className="rounded border border-gray-200 px-2 py-1">
            {name}: {configured ? '設定済み' : '未設定'}
          </li>
        ))}
      </ul>

      <div className={GRID}>
        <div className={FIELD}>
          <label className={LABEL} htmlFor="ad-report-frequency">
            配信頻度
          </label>
          <TaskySelect
            id="ad-report-frequency"
            className="h-9 text-sm"
            value={frequency}
            disabled={disabled}
            onChange={(e) => setFrequency(e.target.value as AdReportFrequency)}
          >
            {FREQUENCY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </TaskySelect>
          <span className={HINT}>朝7時に送ります。</span>
        </div>

        <div className={FIELD}>
          <label className={LABEL} htmlFor="ad-report-budget">
            月間予算（円）
          </label>
          <input
            id="ad-report-budget"
            className={INPUT}
            type="number"
            min={0}
            inputMode="numeric"
            value={monthlyBudget}
            disabled={disabled}
            placeholder="未設定"
            onChange={(e) => setMonthlyBudget(e.target.value)}
          />
        </div>

        <div className={FIELD}>
          <label className={LABEL} htmlFor="ad-report-cpa">
            目標CPA（円）
          </label>
          <input
            id="ad-report-cpa"
            className={INPUT}
            type="number"
            min={0}
            inputMode="numeric"
            value={targetCpa}
            disabled={disabled}
            placeholder="未設定"
            onChange={(e) => setTargetCpa(e.target.value)}
          />
        </div>

        <div className={FIELD}>
          <label className={LABEL} htmlFor="ad-report-discord">
            Discord Webhook URL
          </label>
          <input
            id="ad-report-discord"
            className={INPUT}
            type="password"
            autoComplete="new-password"
            value={discordWebhook}
            disabled={disabled}
            placeholder={setting?.has_discord_webhook ? SECRET_PLACEHOLDER : 'https://discord.com/api/webhooks/...'}
            onChange={(e) => setDiscordWebhook(e.target.value)}
          />
        </div>

        <div className={FIELD}>
          <label className={LABEL} htmlFor="ad-report-slack">
            Slack Webhook URL
          </label>
          <input
            id="ad-report-slack"
            className={INPUT}
            type="password"
            autoComplete="new-password"
            value={slackWebhook}
            disabled={disabled}
            placeholder={setting?.has_slack_webhook ? SECRET_PLACEHOLDER : 'https://hooks.slack.com/services/...'}
            onChange={(e) => setSlackWebhook(e.target.value)}
          />
        </div>

        <div className={FIELD}>
          <label className={LABEL} htmlFor="ad-report-chatwork-room">
            Chatwork ルームID
          </label>
          <input
            id="ad-report-chatwork-room"
            className={INPUT}
            type="text"
            value={chatworkRoomId}
            disabled={disabled}
            placeholder="123456789"
            onChange={(e) => setChatworkRoomId(e.target.value)}
          />
          <span className={HINT}>受信するチャットのルームIDと、そのルームに参加しているアカウントのAPIトークンを設定してください。</span>
        </div>

        <div className={FIELD}>
          <label className={LABEL} htmlFor="ad-report-chatwork-token">
            Chatwork APIトークン
          </label>
          <input
            id="ad-report-chatwork-token"
            className={INPUT}
            type="password"
            autoComplete="new-password"
            value={chatworkToken}
            disabled={disabled}
            placeholder={setting?.has_chatwork_token ? SECRET_PLACEHOLDER : '未設定'}
            onChange={(e) => setChatworkToken(e.target.value)}
          />
        </div>

        <div className={FIELD}>
          <label className={LABEL} htmlFor="ad-report-emails">
            メール宛先
          </label>
          <input
            id="ad-report-emails"
            className={INPUT}
            type="text"
            value={emails}
            disabled={disabled}
            placeholder="ops@example.com, marketing@example.com"
            onChange={(e) => setEmails(e.target.value)}
          />
          <span className={HINT}>カンマ区切りで最大10件。</span>
        </div>
      </div>

      <div className={FOOT}>
        <Button size="sm" disabled={disabled} onClick={() => save()}>
          {saving ? '保存中...' : '保存'}
        </Button>
        <Button variant="outline" size="sm" disabled={disabled || hasUnsavedChanges || !hasSavedDestination} onClick={sendTest}>
          {testing ? '送信中...' : 'テスト送信'}
        </Button>
        {!canEdit && <span className={HINT}>変更できるのは組織のリーダーのみです。</span>}
        {canEdit && !hasDestination && <span className={HINT}>通知先を1つ以上設定してください。</span>}
        {canEdit && !hasUnsavedChanges && hasSavedDestination && <span className={HINT}>保存済みのすべての宛先へ実際に送信します。</span>}
        {message && (
          <span className={message.ok ? STATUS_OK : STATUS_NG} role="status">
            {message.text}
          </span>
        )}
      </div>
    </div>
  );
};

export default AdReportSettingsPanel;
